import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { ProjectUserModel, UserService } from '../../services/user.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AssignUsers } from '../assign-users/assign-users';
import { MessageService } from 'primeng/api';
import { Toast } from "primeng/toast";
import { ProjectService } from '../../services/project.service';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';


@Component({
  selector: 'app-projectuser',
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule, ToggleSwitch, FormsModule, Toast],
  templateUrl: './projectuser.html',
  styleUrls: ['./projectuser.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectUserComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  projectUserList: ProjectUserModel[] = [];
  filteredProjectUserList: ProjectUserModel[] = [];
  loading: boolean = false;
  searchValue: string = '';
  projectId: number = 0;
  dialogRef: DynamicDialogRef | null = null;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;

  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    private projectStateService: ProjectStateService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private permissionHelper: PermissionHelperService
  ) {}


  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const projectIdParam = params.get('projectId');
      if (projectIdParam) {
        const nextProjectId = parseInt(projectIdParam);
        this.projectId = nextProjectId;
        console.log('Project ID from URL:', this.projectId);

        if (this.projectId > 0) {
          this.tryLoadUsers();
        } else {
          console.error('Invalid project ID:', this.projectId);
        }
      } else {
        console.error('No project ID found in URL parameters');
      }
    });

    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.applySearch(search);
    });

    this.projectStateService.currentProjectUserActive$.pipe(takeUntil(this.destroy$)).subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.PROJECT_USERS, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.PROJECT_USERS, PERMISSION_KEYS.CREATE);
      this.canUpdate = this.permissionHelper.has(PAGE_KEYS.PROJECT_USERS, PERMISSION_KEYS.UPDATE);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.PROJECT_USERS, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadUsers();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredProjectUserList = [...this.projectUserList];
    } else {
      this.filteredProjectUserList = this.projectUserList.filter(u =>
        (u.firstName || '').toLowerCase().includes(term) ||
        (u.lastName || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.roleName || '').toLowerCase().includes(term)
      );
    }
    this.cdr.markForCheck();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canUpdate && !this.canDelete);
    this.cdr.markForCheck();
  }

  private tryLoadUsers(): void {
    if (!this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.loadUsers();
  }


  loadUsers(): void {
    this.loading = true;
    this.userService.getProjectUserList(this.projectId).subscribe({
      next: (users) => {
        console.log('Loaded users:', users);
        this.projectUserList = users;
        this.applySearch(this.projectStateService.getPageSearch());
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.projectUserList = [];
        this.filteredProjectUserList = [];
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private refreshProjectUsers(notifyProjectState: boolean = true): void {
    this.loadUsers();
    if (notifyProjectState) {
      this.projectStateService.notifyProjectUsersRefresh(this.projectId);
      this.projectStateService.notifyProjectListRefresh();
    }
  }

  getUserAccentColor(user: ProjectUserModel, _index: number): string {
    return user.isActive ? '#55a55e' : '#ed1c4d';
  }

  getRoleTagClass(roleName: string | null | undefined): string {
    if (!roleName) return '';
    return roleName.toLowerCase().includes('admin') ? 'admin' : '';
  }

  editUser(user: ProjectUserModel): void {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    console.log('Opening Edit User dialog for:', user);

    this.dialogRef = this.dialogService.open(AssignUsers, {
      header: 'Edit User Role',
      width: '35rem',
      height: 'auto',
      modal: true,
      maximizable: false,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'hidden',
        padding: '0'
      },
      data: {
        projectId: this.projectId,
        isEditMode: true,
        user: user
      }
    });

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('User role updated successfully');
        }
        this.refreshProjectUsers();
      },
      error: (error) => {
        console.error('Dialog error:', error);
      }
    });
  }

  deleteUser(user: ProjectUserModel): void {
    if (!this.canDelete || this.isProjectInactive) {
      return;
    }
    if (!confirm(`Are you sure you want to remove ${user.firstName} ${user.lastName} from this project?`)) {
      return;
    }

    console.log('Deleting user from project:', user);

    this.projectService.deleteProjectUser(this.projectId, user.userId!).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `${user.firstName} ${user.lastName} removed from project`
          });
          this.refreshProjectUsers();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to remove user from project'
          });
        }
      },
      error: (error) => {
        console.error('Error deleting user:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove user from project'
        });
      }
    });
  }

  addNewUser(): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    console.log('Opening Assign Users dialog for project:', this.projectId);

    this.dialogRef = this.dialogService.open(AssignUsers, {
      header: 'Assign Users',
      width: '45rem',
      height: '75vh',
      modal: true,
      maximizable: false,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'hidden',
        padding: '0'
      },
      data: {
        projectId: this.projectId
      }
    });

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Users assigned successfully');
        }
        this.refreshProjectUsers();
      },
      error: (error) => {
        console.error('Dialog error:', error);
      }
    });
  }

  toggleUserStatus(user: ProjectUserModel): void {
    if (!this.canUpdate || this.isProjectInactive) {
      user.isActive = !user.isActive;
      this.cdr.markForCheck();
      return;
    }
    const newStatus = user.isActive;
    const statusText = newStatus ? 'activate' : 'deactivate';

    console.log(`Toggling user status to ${statusText}:`, user);

    this.projectService.updateProjectUserStatus(this.projectId, user.userId!, newStatus).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `${user.firstName} ${user.lastName} has been ${statusText}d successfully`
          });
          this.refreshProjectUsers();
        } else {
          // Revert the toggle if the API call failed
          user.isActive = !newStatus;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to ${statusText} user`
          });
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error toggling user status:', error);
        // Revert the toggle on error
        user.isActive = !newStatus;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${statusText} user. Please try again.`
        });
        this.cdr.markForCheck();
      }
    });
  }

  toggleUserOnField(user: ProjectUserModel): void {
    if (!this.canUpdate || this.isProjectInactive) {
      user.onField = !user.onField;
      this.cdr.markForCheck();
      return;
    }

    const newValue = !!user.onField;
    const statusText = newValue ? 'marked as on field' : 'marked as off field';

    this.projectService.updateProjectUserOnField(this.projectId, user.userId!, newValue).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `${user.firstName} ${user.lastName} has been ${statusText}`
          });
          this.refreshProjectUsers();
        } else {
          user.onField = !newValue;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update on-field status'
          });
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error toggling user on-field status:', error);
        user.onField = !newValue;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update on-field status. Please try again.'
        });
        this.cdr.markForCheck();
      }
    });
  }
}
